import React, { useEffect } from "react";
import {
  DEFAULT_EQUIPMENT,
  DEFAULT_LABORATORY,
  DEFAULT_VALID_PREPARATION,
  DEFAULT_AMOUNT_SAMPLE,
} from "@app/pages/samples/utils/constants";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ProcedureType, UserType } from "@app/types/genericTables.types";
import {
  AnalysisCurrent,
  PreparationTypes,
  SampleStateType,
  SampleStateProcessingType,
} from "@app/types/samples.types";
import { useEquipment } from "@services/servicesGenericsTables";
import useTabs from "@app/pages/samples/utils/useTab";
import { useSample } from "@app/pages/samples/utils/useSample";
import useAuth from "@app/context/AuthContext";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { formatDateOrNow } from "@app/utils/formatDate";
import { setsAnalysisPhasesBR } from "@app/pages/samples/config/phases/AnalysisPhases";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";

const getDefaultValues = (
  currentPreparationIndex?: PreparationTypes,
  currentMeasurement?: AnalysisCurrent,
  currentCounting?: AnalysisCurrent,
  currentCalculation?: AnalysisCurrent,
  sampling?: SampleStateType,
  user?: UserType,
  typeAnalysis?: any,
  filteredProceduresPRE?: ProcedureType,
  filteredProceduresAA?: ProcedureType,
  processing?: SampleStateProcessingType
) => {
  const currentMeasurementIndex: any =
    currentPreparationIndex?.aa_measures?.[currentMeasurement?.index as number];

  const currentCountingIndex: any =
    currentMeasurementIndex?.countings?.[currentCounting?.index as number];
  const currentCalculationIndex =
    currentCountingIndex?.calculations[currentCalculation?.index as number];
  const currentSampling = sampling?.current;
  const defaultTypeUnit = sampling?.current?.unit_types;

  const consultEquipments = (ids: number[]) => {
    if (!ids) return [];
    return ids.map((id) =>
      useEquipment
        .getState()
        .equipmentList.find((equipment) => equipment.id === id)
    );
  };

  const processingArray = processing?.current ?? [];

  const wetDryValue = processingArray.find(
    (item) => item.wet_dry !== null && item.wet_dry !== undefined
  )?.wet_dry;

  return {
    analysis_analyst: currentPreparationIndex?.analyst,
    //preparation
    preparation_procedure:
      currentPreparationIndex?.preparation_procedure ||
      filteredProceduresPRE ||
      undefined,
    start_date: currentPreparationIndex?.start_date || dayjs().toISOString(),
    analysis_amount_sample: currentPreparationIndex?.amount_sample || 0,
    analysis_equipment: currentPreparationIndex?.equipment || [],
    scintillator_lot_number:
      currentPreparationIndex?.scintillator_lot_number || undefined,
    analysis_unit_types: currentPreparationIndex?.unit_types || defaultTypeUnit,
    valid_preparation:
      currentPreparationIndex?.valid_preparation || DEFAULT_VALID_PREPARATION,
    mg_torta: 300,
    planchette: currentPreparationIndex?.planchette,
    planchette_with_precipitate:
      currentPreparationIndex?.planchette_with_precipitate,
    planchette_with_precipitate_1:
      currentPreparationIndex?.planchette_with_precipitate_1,
    planchette_with_precipitate_2:
      currentPreparationIndex?.planchette_with_precipitate_2,
    planchette_with_precipitate_3:
      currentPreparationIndex?.planchette_with_precipitate_3,
    planchette_with_precipitate_4:
      currentPreparationIndex?.planchette_with_precipitate_4,
    planchette_with_precipitate_5:
      currentPreparationIndex?.planchette_with_precipitate_5,
    //mesurement
    measurement_analyst: currentMeasurementIndex?.analyst || user,
    measurement_procedure: currentMeasurementIndex?.procedure || undefined,
    measurement_start_date_time: formatDateOrNow(
      currentMeasurementIndex?.start_date_time
    ),
    measurement_equipment:
      consultEquipments(
        currentMeasurementIndex?.equipment?.map((e: any) => e.id)
      ) || [],
    measurement_dilution_factor:
      currentMeasurementIndex?.dilution_factor || null,
    measurement_aa_equipment_result:
      currentMeasurementIndex?.aa_equipment_result || null,
    measurement_result_to_calculation:
      currentMeasurementIndex?.result_to_calculation || null,
    measurement_mg_potasio: currentMeasurementIndex?.mg_potasio || null,
    //counting
    counting_analyst: currentCountingIndex?.analyst || user,
    counting_procedure: currentCountingIndex?.counting_procedure || undefined,
    counting_equipment: currentCountingIndex?.equipment
      ? currentCountingIndex?.equipment
      : typeAnalysis
      ? DEFAULT_EQUIPMENT[`${typeAnalysis?.id}`]
      : [],
    counting_detector: currentCountingIndex?.detectors || undefined,
    counting_date: currentCountingIndex?.counting_date || dayjs().toISOString(),
    total_sample_counting_time:
      currentCountingIndex?.total_sample_counting_time || undefined,
    first_counting_cpm: currentCountingIndex?.first_counting_cpm || undefined,
    second_counting_cpm: currentCountingIndex?.second_counting_cpm || undefined,
    counting_valid: "NA",

    //calculation
    laboratory: currentCalculationIndex?.laboratory || DEFAULT_LABORATORY,
    calculation_equipment: currentCountingIndex?.equipment || [
      currentCountingIndex?.equipo,
    ],
    calculation_analyst: currentCalculationIndex?.analyst || user,
    start_date_time: formatDateOrNow(currentSampling?.date_start_sampling),
    end_date_time: formatDateOrNow(currentSampling?.date_end_sampling),
    calculation_amount_sample:
      currentPreparationIndex?.amount_sample || undefined,
    calculation_units: currentPreparationIndex?.unit_types || undefined,
    calculation_planchette_with_precipitate:
      currentPreparationIndex?.planchette_with_precipitate || undefined,
    calculation_mg_potasio: currentMeasurementIndex?.mg_potasio || null,
    //calculation_dry_wet_weight:currentCountingIndex?.calculation_dry_wet_weight ||getProcessingCalculation(latestObject),
    calculation_dry_wet_weight: wetDryValue || "",
    calculation_procedure:
      currentCalculationIndex?.calculation_procedure || undefined,

    calculation_detector: currentCountingIndex?.detectors || undefined,
    calculation_first_counting_date: formatDateOrNow(
      currentCountingIndex?.first_counting_date
    ),
    calculation_counting_date: currentCountingIndex?.counting_date
      ? formatDateOrNow(currentCountingIndex?.counting_date)
      : undefined,
    total_time_counting:
      currentCalculationIndex?.total_time_counting ||
      currentCountingIndex?.total_sample_counting_time,
    calculation: currentCalculationIndex?.calculation || "",
    calculation_date: formatDateOrNow(
      currentCalculationIndex?.calculation_date
    ),
    calculation_fondo: currentCalculationIndex?.fondos || undefined,
    calculation_fondo_alfa: currentCalculationIndex?.fondo_alfa || undefined,
    calculation_method: currentCalculationIndex?.calculation_method || "ISO",
    idResultPreload: currentCalculationIndex?.result?.id || undefined,
  };
};

const BRForm = () => {
  const { tabValueAnalysis } = useTabs();
  const filteredProceduresPRE = useFilteredProcedures("PRE");
  const filteredProceduresAA = useFilteredProcedures("AA");

  const form = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { sampling } = stateSample;
  const { processing } = stateSample;
  const { user } = useAuth();

  const {
    currentPreparation,
    currentMeasurement,
    preparationsList,
    currentCounting,
    currentCalculation,
  } = useAnalysis();

  const currentPreparationObject = preparationsList.filter((preparation) =>
    preparation.analysis_type.some(
      (analysis) => analysis.id === form.getValues("typeAnalysis")?.id
    )
  )[currentPreparation.index as number];

  useEffect(() => {
    if (currentPreparationObject) {
      const currentValues = form.getValues();

      const newValues = {
        ...currentValues,
        ...getDefaultValues(
          currentPreparationObject,
          currentMeasurement,
          currentCounting,
          currentCalculation,
          sampling,
          user,
          form.getValues("typeAnalysis"),
          filteredProceduresPRE?.[0],
          filteredProceduresAA?.[0],
          processing
        ),
      };

      form.reset(newValues);
    }
  }, [idMuestra, JSON.stringify(currentPreparationObject), currentCounting]); // eslint-disable-line react-hooks/exhaustive-deps

  const CurrentComponent = setsAnalysisPhasesBR[tabValueAnalysis]?.component;
  return CurrentComponent ? <CurrentComponent /> : null;
};

export default BRForm;
